import { useEffect, useMemo, useState } from 'react';
import { ApiRequest } from '@/utils/axios';
import { inventoryStore } from '@/stores/inventory.store';
import { modalStore } from '@/stores/modal.store';
import { coinflipStore, coinflipIntent } from '@/stores/coinflip.store';
import { ECoinflipSide, ICoinflipGame } from '@/utils/types/ICoinflipGame';
import { itemsValue } from '@/utils/itemInfo';
import { notify } from '@/utils/notify';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import CoinflipInventoryPicker from '@/components/Modals/Inventory/CoinflipInventoryPicker';
import CoinflipJoinFooter from '@/components/Modals/Inventory/CoinflipJoinFooter';

interface Props {
    id: number;
}

const JoinCoinflip = ({ id }: Props) => {
    const {
        inventoryItems,
        selectedItemIds,
        maxSelectedItems,
        setSelectedById,
        clearInventory,
        loadSiteInventoryDeposit,
    } = inventoryStore((state) => state);
    const { activeGames, games } = coinflipStore((state) => state);
    const { toggleModal } = modalStore((state) => state);

    const [loading, setLoading] = useState(true);
    const [coinflip, setCoinflip] = useState<ICoinflipGame>();

    const joinSide =
        coinflip?.firstSide === ECoinflipSide.FIRST ? ECoinflipSide.SECOND : ECoinflipSide.FIRST;

    useEffect(() => {
        setCoinflip(activeGames().find((g) => g?.id === id) ?? games.get(id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, games]);

    useEffect(() => {
        loadSiteInventoryDeposit().finally(() => setLoading(false));
        return () => clearInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (coinflip?.secondUser) {
            toggleModal(false);
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title="Error" text="This coinflip is started" />
            );
        } else if (coinflip?.pendingJoin) {
            toggleModal(false);
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title="Error" text="Another player is joining this coinflip" />
            );
        }
    }, [coinflip?.secondUser, coinflip?.pendingJoin]);

    const opponentValue = useMemo(
        () => (coinflip ? itemsValue(coinflip.firstItems) : 0),
        [coinflip]
    );
    const range: [number, number] = useMemo(
        () => [Number((opponentValue * 0.9).toFixed(2)), Number((opponentValue * 1.1).toFixed(2))],
        [opponentValue]
    );

    const pickerItems = inventoryItems.map((it) => ({
        id: it.id,
        name: it.item.name,
        priceUsd: it.item.priceUsd,
        colorHex: it.item.colorHex,
        icon: it.item.icon,
        iconBackground: it.item.iconBackground,
    }));
    const inventoryTotal = pickerItems.reduce((s, i) => s + i.priceUsd, 0);
    const selected = pickerItems.filter((i) => selectedItemIds.has(i.id));
    const selectedAmount = selected.reduce((s, i) => s + i.priceUsd, 0);
    const inRange = selectedAmount >= range[0] && selectedAmount <= range[1];
    const chance = selectedAmount > 0 ? (selectedAmount / (selectedAmount + opponentValue)) * 100 : 0;
    const canJoin = selected.length > 0 && inRange;

    async function join() {
        if (!canJoin || !coinflip) return;
        // Mark this tab as the initiator so the coinflipJoined broadcast only
        // opens the game here, not on the user's other open tabs.
        const gameId = Number(coinflip.id);
        coinflipIntent.joinIds.add(gameId);
        const inventoryIds = Array.from(selectedItemIds);
        await ApiRequest.post('/coinflip/join/inventory', {
            inventoryIds,
            side: joinSide,
            gameId,
        });
    }

    if (!coinflip) return null;

    return (
        <CoinflipInventoryPicker
            items={pickerItems}
            selectedItemIds={selectedItemIds}
            setSelectedById={setSelectedById}
            maxItems={maxSelectedItems}
            loading={loading}
            onRefresh={() => {
                setLoading(true);
                clearInventory();
                loadSiteInventoryDeposit().finally(() => setLoading(false));
            }}
            footer={
                <CoinflipJoinFooter
                    inventoryTotal={inventoryTotal}
                    selectedCount={selected.length}
                    selectedAmount={selectedAmount}
                    range={range}
                    chance={chance}
                    onAction={join}
                    actionLabel="JOIN"
                    actionDisabled={!canJoin}
                />
            }
        />
    );
};

export default JoinCoinflip;
